import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { PageHeader, PageBody } from "@/components/AppShell";
import { KpiCard, SkeletonCard } from "@/components/Kpi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Clock, TrendingUp, AlertCircle, Store, Info, Gauge as GaugeIcon, Euro, CheckCircle2, XCircle,
  Mail, Loader2, Send, User, AtSign, Server, CornerUpLeft, Link2, Copy,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RecruiterAvatar } from "@/components/RecruiterAvatar";
import { frenchDate } from "@/lib/shariti";

const STATUT_TONE = {
  "Approuvé": "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
  "En attente": "bg-[rgba(252,219,83,0.4)] text-[#5A4200]",
  "Invalide": "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
  "Annulé": "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
  "Alien": "bg-[rgba(24,24,24,0.06)] text-[rgba(24,24,24,0.6)]",
};

// Ligne « libellé : valeur » avec icône — récap de l'e-mail dans le dialogue de renvoi.
const InfoLine = ({ icon: Icon, label, value, testid, strong = false, mono = false }) => (
  <div className="flex items-start gap-2">
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-ink" />
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-ink">{label}</div>
      <div className={`break-words ${strong ? "font-bold text-ink" : "text-ink"} ${mono ? "font-mono text-[11px]" : "text-[13px]"}`}
        data-testid={testid}>{value || "—"}</div>
    </div>
  </div>
);

export default function RdFiche() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Renvoi du lien personnel : dialogue d'aperçu -> confirmation d'envoi.
  const [resendOpen, setResendOpen] = useState(false);
  const [preview, setPreview] = useState(null);        // aperçu (aucun envoi)
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);              // résultat après envoi réel

  useEffect(() => {
    api.get(`/rds/${id}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [id]);

  // Ouvre le dialogue et charge l'APERÇU (destinataire, objet, expéditeur, rendu HTML)
  // — sans envoyer aucun e-mail.
  const openResend = async () => {
    setResendOpen(true);
    setSent(null);
    setPreviewError("");
    setPreview(null);
    setPreviewLoading(true);
    try {
      const { data: res } = await api.get(`/rds/${id}/resend-link/preview`);
      setPreview(res);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || "Impossible de charger l'aperçu de l'e-mail.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Envoi RÉEL après validation dans le dialogue. Affiche une confirmation détaillée
  // (depuis quelle boîte l'e-mail part, à qui, quel objet) + toast de succès.
  const doSend = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/rds/${id}/resend-link`);
      setSent(res);
      toast.success(`Lien personnel envoyé à ${res.to}`, {
        description: `Depuis ${res.from_email || "la boîte d'envoi"} · objet : ${res.subject || ""}`,
      });
      // Rafraîchit la fiche pour mettre à jour le badge « Lien renvoyé le … ».
      api.get(`/rds/${id}`).then((r) => setData(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) return (
    <PageBody><div className="py-16 text-center text-muted-ink">RD introuvable.</div></PageBody>
  );

  const rd = data?.rd;
  // Avatar en-tête : photo du collaborateur (upload admin → lien admin → PREREC) via l'endpoint
  // public (repli initiales automatique si 404 / pas de photo).
  const rdHasPhoto = !!(rd && (rd.photo_path || rd.photo_url || rd.prerec_photo_url));
  const rdPhotoSrc = rdHasPhoto
    ? `${process.env.REACT_APP_BACKEND_URL}/api/pointage/public/rd-photo/${id}`
    : null;
  const s = data?.stats;
  const hasHours = s?.has_hours_data;

  // Le renvoi n'est possible que si le collaborateur a un e-mail ET un lien personnel
  // actif (token présent, non révoqué) et n'est pas archivé.
  const hasEmail = !!(rd && (rd.email || "").trim());
  const hasActiveLink = !!(rd && rd.pointage_token && !rd.pointage_token_revoked && !rd.archived_at);
  const canResend = hasEmail && hasActiveLink;
  const resendDisabledReason = !hasEmail
    ? "Ajoutez une adresse e-mail à ce collaborateur pour lui renvoyer son lien."
    : (rd && rd.archived_at)
      ? "Collaborateur archivé : lien personnel indisponible."
      : !hasActiveLink
        ? "Ce collaborateur n'a pas de lien personnel actif (lien absent ou révoqué)."
        : "";

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <RecruiterAvatar name={rd?.name} src={rdPhotoSrc} size={52} testid="rd-fiche-avatar" />
            <span className="break-words">{rd?.name || "Recruteur"}</span>
          </span>
        }
        subtitle={rd ? `${rd.code ? `Code ${rd.code} · ` : ""}Performance et suivi` : "Chargement…"}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {rd && (
              <Button
                onClick={openResend}
                disabled={!canResend}
                title={canResend ? "Prévisualiser puis renvoyer par e-mail le lien personnel" : resendDisabledReason}
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-50"
                data-testid="rd-resend-link">
                <Send className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Renvoyer le lien personnel</span>
                <span className="sm:hidden">Renvoyer le lien</span>
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate(-1)} className="border border-[var(--border-soft)] bg-white" data-testid="rd-back">
              <ArrowLeft className="mr-2 h-4 w-4" /> Retour
            </Button>
          </div>
        }>
        {rd && (
          <div className="flex flex-wrap items-center gap-2">
            {rd.statut === "a_completer"
              ? <Badge className="bg-[rgba(233,184,0,0.35)] text-[#5A4200]" data-testid="rd-statut">Fiche à compléter</Badge>
              : <Badge className="bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" data-testid="rd-statut">RD actif</Badge>}
            {(data?.associations || []).map((a) => (
              <Badge key={a.id} variant="secondary" className="bg-white/70">{a.acronym}</Badge>
            ))}
            {rd.phone && <span className="text-xs text-[rgba(24,24,24,0.6)]">{rd.phone}</span>}
            {hasEmail && (
              <span className="inline-flex items-center gap-1 text-xs text-[rgba(24,24,24,0.6)]" data-testid="rd-email">
                <Mail className="h-3.5 w-3.5" />{rd.email}
              </span>
            )}
            {rd.onboarding_email_last_resent_at ? (
              <Badge className="gap-1 bg-[rgba(238,114,46,0.12)] text-[10px] text-[#8A3A10]" data-testid="rd-link-sent">
                <CheckCircle2 className="h-3 w-3" /> Lien renvoyé le {frenchDate(rd.onboarding_email_last_resent_at)}
              </Badge>
            ) : (rd.onboarding_email_sent_at && rd.onboarding_email_sent_at !== "seed") ? (
              <Badge className="gap-1 bg-[rgba(31,157,85,0.10)] text-[10px] text-[#0F5A2F]" data-testid="rd-link-sent">
                <CheckCircle2 className="h-3 w-3" /> Lien envoyé le {frenchDate(rd.onboarding_email_sent_at)}
              </Badge>
            ) : null}
          </div>
        )}
      </PageHeader>

      <PageBody>
        {!data ? (
          <div className="grid gap-4 @2xl/page:grid-cols-2 @4xl/page:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}</div>
        ) : (
          <div className="space-y-5">
            {/* Métrique centrale : bulletins par heure travaillée */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-6 shadow-[var(--shadow-sm)]" data-testid="rd-central-metric">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[rgba(238,114,46,0.12)]"><GaugeIcon className="h-7 w-7 text-[var(--shariti-orange)]" /></div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-ink">Métrique centrale · Bulletins par heure travaillée</div>
                    {hasHours ? (
                      <div className="mt-1 flex items-end gap-2">
                        <span className="text-4xl font-extrabold tracking-tight" data-testid="rd-per-hour">{s.bulletins_par_heure}</span>
                        <span className="mb-1 text-sm font-semibold text-muted-ink">bull./h · sur {s.heures_travaillees} h travaillées</span>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-2 text-[#9B6A00]" data-testid="rd-per-hour-insufficient">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-bold">Données insuffisantes</span>
                        <span className="text-xs text-muted-ink">— aucune heure de présence saisie pour ce RD.</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-ink">Bulletins mesurés (avec heures)</div>
                  <div className="text-lg font-extrabold">{s.measured_bulletins} <span className="text-sm font-semibold text-muted-ink">/ {s.total_bulletins}</span></div>
                </div>
              </div>
              {s.unmeasured_bulletins > 0 && (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-[rgba(233,184,0,0.4)] bg-[rgba(252,219,83,0.14)] p-3 text-xs text-[#5A4200]" data-testid="rd-insufficient-banner">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span><span className="font-bold">{s.unmeasured_bulletins} bulletin(s)</span> sans heures de présence (import Evergiving) sont exclus du calcul bulletins/heure et du taux de conversion. Ces indicateurs ne sont pas approximés — ils nécessitent une saisie manuelle avec heures.</span>
                </div>
              )}
            </div>

            {/* KPI grid */}
            <div className="grid gap-4 @2xl/page:grid-cols-2 @4xl/page:grid-cols-4">
              <KpiCard label="Bulletins comptés (alignés Evergiving)" value={s.total_bulletins_net ?? s.total_bulletins} accent="#2F6FED" hint={(s.total_bulletins_net ?? s.total_bulletins) !== s.total_bulletins ? `${s.total_bulletins} au total · ${s.total_bulletins - (s.total_bulletins_net ?? s.total_bulletins)} non comptés (invalides, annulés ou périmés)` : "= décompte Evergiving"} testId="rd-kpi-total" />
              <KpiCard label="Taux de validation" value={(s.taux_validation ?? 0) * 100} suffix="%" decimals={1} accent="#1F9D55" hint={`${s.approuves} approuvés`} testId="rd-kpi-validation" />
              <KpiCard label="Taux d'annulation" value={(s.taux_annulation ?? 0) * 100} suffix="%" decimals={1} accent="#D64545" hint={`${s.annules} annulés/invalides`} testId="rd-kpi-annulation" />
              <KpiCard label="Montant cumulé" value={s.montant_total} suffix="€" accent="#8B5CF6" hint={s.montant_moyen ? `moy. ${s.montant_moyen} €` : ""} testId="rd-kpi-montant" />
            </div>

            <div className="grid gap-5 @4xl/page:grid-cols-3">
              {/* Tendance */}
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 @4xl/page:col-span-2">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold"><TrendingUp className="h-4 w-4 text-[var(--shariti-orange)]" /> Tendance mensuelle</h3>
                {s.trend.length === 0 ? (
                  <div className="py-10 text-center text-xs text-muted-ink">Aucune donnée.</div>
                ) : (
                  <div className="h-56" data-testid="rd-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={s.trend} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,24,24,0.06)" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(24,24,24,0.1)", fontSize: 12 }} />
                        <Bar dataKey="count" name="Bulletins" fill="#EE722E" radius={[6, 6, 0, 0]} maxBarSize={44} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Statuts */}
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
                <h3 className="mb-3 text-sm font-bold">Répartition par statut</h3>
                <div className="space-y-1.5 text-xs" data-testid="rd-statut-breakdown">
                  {Object.entries(s.by_statut || {}).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
                    <div key={st} className="flex items-center justify-between">
                      <span className={`rounded px-2 py-0.5 font-semibold ${STATUT_TONE[st] || "bg-[var(--surface-2)]"}`}>{st}</span>
                      <span className="font-extrabold">{n.toLocaleString("fr-FR")}</span>
                    </div>
                  ))}
                  {Object.keys(s.by_statut || {}).length === 0 && <div className="text-muted-ink">Aucun bulletin.</div>}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[var(--border-soft)] pt-3 text-xs">
                  <span className="flex items-center gap-1.5 text-muted-ink"><Clock className="h-3.5 w-3.5" /> Heures travaillées</span>
                  <span className="font-extrabold">{s.heures_travaillees} h</span>
                </div>
              </div>
            </div>

            {/* Top spots */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-bold"><Store className="h-4 w-4 text-[var(--shariti-orange)]" /> Spots les plus travaillés</h3>
              {s.top_spots.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-ink">Aucun spot.</div>
              ) : (
                <div className="grid gap-2 @2xl/page:grid-cols-2" data-testid="rd-top-spots">
                  {s.top_spots.map((sp) => (
                    <button key={sp.spot_id} onClick={() => navigate(`/spots/${sp.spot_id}`)}
                      className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-left transition-colors hover:border-[var(--shariti-orange)]"
                      data-testid={`rd-spot-${sp.spot_id}`}>
                      <span className="min-w-0 break-words text-xs font-semibold">{sp.spot_name}</span>
                      <Badge variant="secondary" className="ml-2 shrink-0 bg-white">{sp.count}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </PageBody>

      {/* Dialogue : aperçu de l'e-mail + confirmation d'envoi du lien personnel */}
      <Dialog open={resendOpen} onOpenChange={(o) => { if (!sending) setResendOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rd-resend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[var(--shariti-orange)]" /> Renvoyer le lien personnel
            </DialogTitle>
            <DialogDescription>
              Vérifiez l'e-mail exact qui sera envoyé (expéditeur, destinataire, objet et aperçu),
              puis validez l'envoi. Aucun e-mail n'est envoyé tant que vous n'avez pas cliqué sur « Envoyer maintenant ».
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="grid place-items-center py-12" data-testid="rd-resend-loading">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--shariti-orange)]" />
            </div>
          ) : previewError ? (
            <div className="flex items-start gap-2 rounded-xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] p-3 text-sm text-[#9B2226]" data-testid="rd-resend-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {previewError}
            </div>
          ) : preview ? (
            <div className="space-y-3">
              {sent && (
                <div className="flex items-start gap-2 rounded-xl border border-[rgba(31,157,85,0.4)] bg-[rgba(31,157,85,0.08)] p-3 text-sm text-[#0F5A2F]" data-testid="rd-resend-sent">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <div className="font-bold">E-mail envoyé avec succès</div>
                    <div className="text-[13px]">
                      À <b>{sent.to}</b> · depuis <b>{sent.from_email || preview.from_email}</b>
                      {sent.fallback ? " (compte d'envoi)" : ""} · via {sent.provider_label || sent.provider || preview.provider_label}
                      {(sent.copy_email || preview.copy_email) && <> · copie à <b>{sent.copy_email || preview.copy_email}</b></>}
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <InfoLine icon={User} label="De (vu par le collaborateur)" value={`${preview.from_name} <${preview.from_email}>`} testid="rd-resend-from" strong />
                  <InfoLine icon={AtSign} label="À (destinataire)" value={preview.to} testid="rd-resend-to" strong />
                  <InfoLine icon={Server} label="Service d'envoi" value={preview.provider_label || preview.provider} testid="rd-resend-account" />
                  <InfoLine icon={CornerUpLeft} label="Réponses dirigées vers" value={preview.reply_to} testid="rd-resend-replyto" />
                  {preview.copy_email && (
                    <InfoLine icon={Copy} label="Copie (cachée) à" value={preview.copy_email} testid="rd-resend-copy" />
                  )}
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-ink">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>L'e-mail part réellement de <b className="font-semibold text-ink">{preview.from_email}</b> (compte authentifié Infomaniak) ; c'est cette adresse que verra le collaborateur. Ses réponses éventuelles arriveront sur <b className="font-semibold text-ink">{preview.reply_to}</b>.</span>
                </p>
                <div className="mt-3 border-t border-dashed border-[var(--border-soft)] pt-3">
                  <InfoLine icon={Mail} label="Objet de l'e-mail" value={preview.subject} testid="rd-resend-subject" strong />
                </div>
                <div className="mt-3">
                  <InfoLine icon={Link2} label="Lien personnel inclus" value={preview.link} testid="rd-resend-linkvalue" mono />
                </div>
              </div>

              <div className="text-[11px] font-semibold text-muted-ink">Aperçu de l'e-mail qui sera reçu :</div>
              <iframe title="Aperçu de l'e-mail de bienvenue" sandbox="" data-testid="rd-resend-preview-iframe"
                srcDoc={preview.html || ""} className="h-[420px] w-full rounded-lg border border-[var(--border-soft)] bg-white" />
            </div>
          ) : null}

          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => setResendOpen(false)} disabled={sending} data-testid="rd-resend-cancel">
              {sent ? "Fermer" : "Annuler"}
            </Button>
            {!sent && (
              <Button onClick={doSend} disabled={sending || previewLoading || !!previewError || !preview}
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-50" data-testid="rd-resend-confirm">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Envoyer maintenant
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
